import {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  type CSSProperties,
  type KeyboardEvent,
  type ChangeEvent,
  type FocusEvent,
  type ClipboardEvent,
} from 'react';

// ─── Country data ─────────────────────────────────────────────────────────────

export interface Country {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  dialCode: string; // e.g. "+1"
  flag: string; // emoji flag
  format: string; // "#" = digit placeholder, other chars are literals
  priority?: number; // tie-break for countries sharing a dial code
}

export const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸', format: '(###) ###-####', priority: 0 },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', format: '(###) ###-####', priority: 1 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', format: '#### ######' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺', format: '### ### ###' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', format: '#### ########' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', format: '## ## ## ## ##' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', format: '##### #####' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', format: '### #### ####' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', format: '## #### ####' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷', format: '### #### ####' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷', format: '(##) #####-####' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽', format: '## #### ####' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦', format: '## ### ####' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬', format: '### ### ####' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬', format: '### ### ####' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦', format: '## ### ####' },
  { code: 'AE', name: 'UAE', dialCode: '+971', flag: '🇦🇪', format: '## ### ####' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬', format: '#### ####' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰', format: '#### ####' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿', format: '## ### ####' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', format: '### ### ####' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', format: '### ### ###' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹', format: '### ### ###' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱', format: '## ### ####' },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪', format: '### ## ## ##' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭', format: '## ### ## ##' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪', format: '## ### ## ##' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴', format: '### ## ###' },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰', format: '## ## ## ##' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮', format: '## ### ####' },
  { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱', format: '### ### ###' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', flag: '🇨🇿', format: '### ### ###' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹', format: '### ######' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺', format: '### ###-##-##' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷', format: '### ### ## ##' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭', format: '### ### ####' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭', format: '## ### ####' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩', format: '### #### ####' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾', format: '## #### ####' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳', format: '### ### ####' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰', format: '### #######' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩', format: '#### ######' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷', format: '## #### ####' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴', format: '### ### ####' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱', format: '# #### ####' },
  { code: 'PE', name: 'Peru', dialCode: '+51', flag: '🇵🇪', format: '### ### ###' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱', format: '## ### ####' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪', format: '### ### ###' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭', format: '### ### ####' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380', flag: '🇺🇦', format: '## ### ## ##' },
  { code: 'RO', name: 'Romania', dialCode: '+40', flag: '🇷🇴', format: '### ### ###' },
  { code: 'HU', name: 'Hungary', dialCode: '+36', flag: '🇭🇺', format: '## ### ####' },
  { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷', format: '### ### ####' },
  { code: 'SK', name: 'Slovakia', dialCode: '+421', flag: '🇸🇰', format: '### ### ###' },
  { code: 'HR', name: 'Croatia', dialCode: '+385', flag: '🇭🇷', format: '## ### ####' },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359', flag: '🇧🇬', format: '## ### ####' },
  { code: 'RS', name: 'Serbia', dialCode: '+381', flag: '🇷🇸', format: '## ### ####' },
];

// ─── Utilities ────────────────────────────────────────────────────────────────

function applyFormat(digits: string, format: string): string {
  let idx = 0;
  return format
    .split('')
    .map((ch) => {
      if (ch === '#') return idx < digits.length ? digits[idx++] : '';
      if (idx >= digits.length) return '';
      return ch;
    })
    .join('')
    .trimEnd();
}

function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function maxDigitsForFormat(format: string): number {
  return (format.match(/#/g) || []).length;
}

function inferCountryFromE164(raw: string, countries: Country[]): Country | null {
  const digits = stripNonDigits(raw);
  if (!digits) return null;
  for (let len = 4; len >= 1; len--) {
    const prefix = '+' + digits.slice(0, len);
    const match = countries
      .filter((c) => c.dialCode === prefix)
      .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))[0];
    if (match) return match;
  }
  return null;
}

// ─── Validation ───────────────────────────────────────────────────────────────

export type ValidationState = 'idle' | 'valid' | 'invalid';

function validateDigits(digits: string, format: string): ValidationState {
  if (!digits) return 'idle';
  const max = maxDigitsForFormat(format);
  if (digits.length === max) return 'valid';
  return 'invalid';
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface EssealPhoneInputRef {
  /** Full E.164 value, e.g. "+14155550100" */
  getValue(): string;
  /** Local digits formatted per country, e.g. "(415) 555-0100" */
  getFormattedValue(): string;
  /** ISO country code currently selected, e.g. "US" */
  getCountryCode(): string;
  /** Dial code currently selected, e.g. "+1" */
  getDialCode(): string;
  /** Whether all digit slots are filled */
  isValid(): boolean;
  /** Programmatically clear the digit input */
  clear(): void;
  /** Programmatically focus the digit input */
  focus(): void;
}

export type EssealPhoneInputTheme = Partial<{
  colorBorder: string;
  colorBorderFocus: string;
  colorBorderError: string;
  colorBg: string;
  colorBgDisabled: string;
  colorBgHover: string;
  colorBgDropdown: string;
  colorBgSearch: string;
  colorBgHighlight: string;
  colorText: string;
  colorTextMuted: string;
  colorTextPlaceholder: string;
  colorTextSelected: string;
  colorTextError: string;
  colorTextHint: string;
  colorTextLabel: string;
  colorValid: string;
  colorInvalid: string;
  colorRingFocus: string;
  colorRingError: string;
  colorSeparator: string;
  radiusField: string;
  radiusDropdown: string;
  radiusSearch: string;
  shadowDropdown: string;
  shadowFieldFocus: string;
  shadowFieldError: string;
}>;

export interface EssealPhoneInputProps {
  /** Starting value — E.164 ("+14155550100") or raw digits */
  defaultValue?: string;
  /** Starting country ISO code (overridden when defaultValue contains a dial code) */
  defaultCountry?: string;
  /** Controlled E.164 value */
  value?: string;
  /**
   * Lock the field to a specific country. The flag and dial code are shown
   * as a static badge — no dropdown is rendered.
   */
  countryCode?: string;
  /** Fires on every change: (e164, localFormatted, country, isValid) */
  onChange?: (e164: string, local: string, country: Country, valid: boolean) => void;
  /** Fires on input blur */
  onBlur?: (e164: string, valid: boolean) => void;
  /** Fires on input focus */
  onFocus?: () => void;
  /** Override built-in validation state */
  validation?: ValidationState;
  /** Error message shown below the field */
  error?: string;
  /** Hint text shown below the field (hidden when error is present) */
  hint?: string;
  /** Field label */
  label?: string;
  /** Mark the field as required */
  required?: boolean;
  /** Disable interaction */
  disabled?: boolean;
  /** Make the field read-only */
  readOnly?: boolean;
  /** Placeholder for the number portion */
  placeholder?: string;
  /** Additional inline style on the root wrapper */
  style?: CSSProperties;
  /** tabIndex for interactive elements */
  tabIndex?: number;
  /** id for the <input> element */
  id?: string;
  /** name for the <input> element */
  name?: string;
  /** Country list to use — defaults to all built-in countries */
  countries?: Country[];
  /** Whether to show the country selector button */
  showCountrySelector?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show the validation icon inside the field */
  showValidationIcon?: boolean;
  /**
   * Override any subset of design tokens.
   * e.g. `theme={{ colorBorderFocus: "#f59e0b", shadowFieldFocus: "0 0 0 3px rgba(245,158,11,0.18)" }}`
   */
  theme?: EssealPhoneInputTheme;
}

// ─── Design tokens (all inline, no external CSS) ──────────────────────────────

const TOKEN = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontMono: "'SF Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
  colorBorder: '#d4d4d8',
  colorBorderFocus: '#6366f1',
  colorBorderError: '#f87171',
  colorBg: '#ffffff',
  colorBgDisabled: '#fafafa',
  colorBgHover: '#f4f4f5',
  colorBgDropdown: '#ffffff',
  colorBgSearch: '#f4f4f5',
  colorBgHighlight: '#eef2ff',
  colorBgSelected: '#eef2ff',
  colorText: '#18181b',
  colorTextMuted: '#71717a',
  colorTextPlaceholder: '#a1a1aa',
  colorTextSelected: '#4338ca',
  colorTextError: '#ef4444',
  colorTextHint: '#a1a1aa',
  colorTextLabel: '#3f3f46',
  colorValid: '#10b981',
  colorInvalid: '#f87171',
  colorRingFocus: 'rgba(99,102,241,0.18)',
  colorRingError: 'rgba(239,68,68,0.15)',
  colorSeparator: '#e4e4e7',
  radiusField: '8px',
  radiusDropdown: '12px',
  radiusSearch: '7px',
  shadowDropdown: '0 8px 30px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
  shadowFieldFocus: '0 0 0 3px rgba(99,102,241,0.18)',
  shadowFieldError: '0 0 0 3px rgba(239,68,68,0.15)',
  transitionFast: 'all 120ms ease',
};

const SIZE = {
  sm: { height: 36, fontSize: 13, labelSize: 12, px: 10, flagSize: 16, iconSize: 14 },
  md: { height: 42, fontSize: 14, labelSize: 13, px: 12, flagSize: 18, iconSize: 15 },
  lg: { height: 50, fontSize: 15, labelSize: 14, px: 16, flagSize: 20, iconSize: 17 },
};

// ─── Component ────────────────────────────────────────────────────────────────

const EssealPhoneInput = forwardRef<EssealPhoneInputRef, EssealPhoneInputProps>(function EssealPhoneInput(
  {
    defaultValue = '',
    defaultCountry = 'US',
    value,
    countryCode: controlledCountryCode,
    onChange,
    onBlur,
    onFocus,
    validation,
    error,
    hint,
    label,
    required = false,
    disabled = false,
    readOnly = false,
    placeholder,
    style,
    tabIndex,
    id,
    name,
    countries = COUNTRIES,
    showCountrySelector = true,
    size = 'md',
    showValidationIcon = true,
    theme: themeProp,
  },
  ref,
) {
  const sz = SIZE[size];
  // Merge caller overrides onto defaults — every TOKEN reference below uses T
  const T = themeProp ? { ...TOKEN, ...themeProp } : TOKEN;
  // When countryCode is supplied the selector is locked to a static badge
  const countryLocked = controlledCountryCode !== undefined;

  // ── Resolve initial state ────────────────────────────────────────────────
  const resolveInitialCountry = (): Country => {
    if (controlledCountryCode) {
      return countries.find((c) => c.code === controlledCountryCode) ?? countries[0];
    }
    if (defaultValue) {
      const inferred = inferCountryFromE164(defaultValue, countries);
      if (inferred) return inferred;
    }
    return countries.find((c) => c.code === defaultCountry) ?? countries[0];
  };

  const resolveInitialDigits = (country: Country): string => {
    if (!defaultValue) return '';
    const all = stripNonDigits(defaultValue);
    const dial = stripNonDigits(country.dialCode);
    return all.startsWith(dial) ? all.slice(dial.length) : all;
  };

  const initialCountry = resolveInitialCountry();

  const [selectedCountry, setSelectedCountry] = useState<Country>(initialCountry);
  const [digits, setDigits] = useState<string>(resolveInitialDigits(initialCountry));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [selectorHover, setSelectorHover] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // ── Sync controlled props ────────────────────────────────────────────────
  useEffect(() => {
    if (value === undefined) return;
    const all = stripNonDigits(value);
    const dial = stripNonDigits(selectedCountry.dialCode);
    setDigits(all.startsWith(dial) ? all.slice(dial.length) : all);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!controlledCountryCode) return;
    const found = countries.find((c) => c.code === controlledCountryCode);
    if (found) setSelectedCountry(found);
  }, [controlledCountryCode, countries]);

  // ── Derived values ───────────────────────────────────────────────────────
  const formatted = applyFormat(digits, selectedCountry.format);
  const maxDigits = maxDigitsForFormat(selectedCountry.format);
  const e164 = selectedCountry.dialCode + digits;
  const localValidation = validateDigits(digits, selectedCountry.format);
  const effectiveValidation = validation ?? localValidation;
  const isValidNow = effectiveValidation === 'valid';
  const hasError = !!error || effectiveValidation === 'invalid';

  const filteredCountries = searchQuery
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.dialCode.includes(searchQuery) ||
          c.code.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : countries;

  // ── Imperative handle ────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    getValue: () => e164,
    getFormattedValue: () => formatted,
    getCountryCode: () => selectedCountry.code,
    getDialCode: () => selectedCountry.dialCode,
    isValid: () => isValidNow,
    clear: () => {
      setDigits('');
      onChange?.('', '', selectedCountry, false);
    },
    focus: () => inputRef.current?.focus(),
  }));

  // ── Close on outside click ───────────────────────────────────────────────
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen) {
      setHighlight(0);
      setTimeout(() => searchRef.current?.focus(), 40);
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen || !listRef.current) return;
    const el = listRef.current.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, dropdownOpen]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = stripNonDigits(e.target.value).slice(0, maxDigits);
      setDigits(raw);
      const newE164 = selectedCountry.dialCode + raw;
      const newFormatted = applyFormat(raw, selectedCountry.format);
      const valid = validateDigits(raw, selectedCountry.format) === 'valid';
      onChange?.(newE164, newFormatted, selectedCountry, valid);
    },
    [maxDigits, selectedCountry, onChange],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text');
      if (!countryLocked && pasted.startsWith('+')) {
        const inferred = inferCountryFromE164(pasted, countries);
        if (inferred) {
          const all = stripNonDigits(pasted);
          const dial = stripNonDigits(inferred.dialCode);
          const local = (all.startsWith(dial) ? all.slice(dial.length) : all).slice(
            0,
            maxDigitsForFormat(inferred.format),
          );
          setSelectedCountry(inferred);
          setDigits(local);
          const newE164 = inferred.dialCode + local;
          const newFormatted = applyFormat(local, inferred.format);
          const valid = validateDigits(local, inferred.format) === 'valid';
          onChange?.(newE164, newFormatted, inferred, valid);
          return;
        }
      }
      const raw = stripNonDigits(pasted).slice(0, maxDigits);
      setDigits(raw);
      const newE164 = selectedCountry.dialCode + raw;
      const newFormatted = applyFormat(raw, selectedCountry.format);
      const valid = validateDigits(raw, selectedCountry.format) === 'valid';
      onChange?.(newE164, newFormatted, selectedCountry, valid);
    },
    [countries, maxDigits, selectedCountry, onChange],
  );

  const handleFocus = useCallback(
    (_e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.();
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e164, isValidNow);
    },
    [e164, isValidNow, onBlur],
  );

  const selectCountry = useCallback(
    (country: Country) => {
      setSelectedCountry(country);
      setDropdownOpen(false);
      setSearchQuery('');
      const clamped = digits.slice(0, maxDigitsForFormat(country.format));
      setDigits(clamped);
      const newE164 = country.dialCode + clamped;
      const newFormatted = applyFormat(clamped, country.format);
      const valid = validateDigits(clamped, country.format) === 'valid';
      onChange?.(newE164, newFormatted, country, valid);
      setTimeout(() => inputRef.current?.focus(), 40);
    },
    [digits, onChange],
  );

  const handleDropdownKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, filteredCountries.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCountries[highlight]) selectCountry(filteredCountries[highlight]);
      } else if (e.key === 'Escape') {
        setDropdownOpen(false);
        setSearchQuery('');
      }
    },
    [filteredCountries, highlight, selectCountry],
  );

  // ── Styles ───────────────────────────────────────────────────────────────

  const fieldBorder = hasError ? T.colorBorderError : focused ? T.colorBorderFocus : T.colorBorder;

  const fieldShadow = hasError ? T.shadowFieldError : focused ? T.shadowFieldFocus : '0 1px 2px rgba(0,0,0,0.04)';

  const effectivePlaceholder = placeholder ?? selectedCountry.format.replace(/#/g, '0');

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontFamily: T.fontFamily,
        position: 'relative',
        ...style,
      }}
    >
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          style={{
            fontSize: sz.labelSize,
            fontWeight: 500,
            color: T.colorTextLabel,
            userSelect: 'none',
            display: 'block',
          }}
        >
          {label}
          {required && (
            <span aria-hidden="true" style={{ marginLeft: 3, color: T.colorTextError }}>
              *
            </span>
          )}
        </label>
      )}

      {/* Field row */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'stretch',
          height: sz.height,
          border: `1px solid ${fieldBorder}`,
          borderRadius: T.radiusField,
          background: disabled ? T.colorBgDisabled : T.colorBg,
          boxShadow: fieldShadow,
          transition: T.transitionFast,
          opacity: disabled ? 0.55 : 1,
          overflow: 'visible',
        }}
      >
        {/* Country selector — static badge when locked, interactive dropdown otherwise */}
        {showCountrySelector &&
          (countryLocked ? (
            <div
              aria-label={`Country: ${selectedCountry.name} ${selectedCountry.dialCode}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: `0 ${sz.px}px`,
                borderRight: `1px solid ${T.colorSeparator}`,
                borderRadius: `${T.radiusField} 0 0 ${T.radiusField}`,
                background: 'transparent',
                flexShrink: 0,
                userSelect: 'none',
              }}
            >
              <span role="img" aria-hidden="true" style={{ fontSize: sz.flagSize, lineHeight: 1 }}>
                {selectedCountry.flag}
              </span>
              <span
                style={{
                  fontFamily: T.fontMono,
                  fontSize: sz.fontSize - 1,
                  color: T.colorTextMuted,
                  letterSpacing: '-0.02em',
                }}
              >
                {selectedCountry.dialCode}
              </span>
            </div>
          ) : (
            <button
              type="button"
              aria-label={`Country: ${selectedCountry.name} ${selectedCountry.dialCode}`}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              disabled={disabled || readOnly}
              tabIndex={tabIndex}
              onMouseEnter={() => setSelectorHover(true)}
              onMouseLeave={() => setSelectorHover(false)}
              onClick={() => {
                if (!disabled && !readOnly) setDropdownOpen((o) => !o);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: `0 ${sz.px}px`,
                border: 'none',
                borderRight: `1px solid ${T.colorSeparator}`,
                borderRadius: `${T.radiusField} 0 0 ${T.radiusField}`,
                background: selectorHover && !disabled && !readOnly ? T.colorBgHover : 'transparent',
                cursor: disabled || readOnly ? 'default' : 'pointer',
                transition: T.transitionFast,
                flexShrink: 0,
                outline: 'none',
              }}
            >
              <span role="img" aria-hidden="true" style={{ fontSize: sz.flagSize, lineHeight: 1 }}>
                {selectedCountry.flag}
              </span>
              <span
                style={{
                  fontFamily: T.fontMono,
                  fontSize: sz.fontSize - 1,
                  color: T.colorTextMuted,
                  letterSpacing: '-0.02em',
                }}
              >
                {selectedCountry.dialCode}
              </span>
              <ChevronIcon size={11} color={T.colorTextMuted} rotate={dropdownOpen} />
            </button>
          ))}

        {/* Digit input */}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          required={required}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={
            [error ? `${id}-error` : null, hint && !error ? `${id}-hint` : null].filter(Boolean).join(' ') || undefined
          }
          placeholder={effectivePlaceholder}
          value={formatted}
          onChange={handleInputChange}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            padding: `0 ${sz.px}px`,
            fontFamily: T.fontMono,
            fontSize: sz.fontSize,
            color: T.colorText,
            letterSpacing: '0.04em',
            cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'text',
          }}
        />

        {/* Validation icon */}
        {showValidationIcon && digits.length > 0 && !readOnly && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingRight: sz.px,
              flexShrink: 0,
            }}
          >
            {effectiveValidation === 'valid' && <CheckCircleIcon size={sz.iconSize} color={T.colorValid} />}
            {effectiveValidation === 'invalid' && <AlertCircleIcon size={sz.iconSize} color={T.colorInvalid} />}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {dropdownOpen && showCountrySelector && (
        <div
          role="dialog"
          aria-label="Select country"
          onKeyDown={handleDropdownKeyDown}
          style={{
            position: 'absolute',
            top: label ? sz.height + sz.labelSize + 12 + 6 : sz.height + 6,
            left: 0,
            zIndex: 9999,
            width: 280,
            background: T.colorBgDropdown,
            border: `1px solid ${T.colorBorder}`,
            borderRadius: T.radiusDropdown,
            boxShadow: T.shadowDropdown,
            overflow: 'hidden',
          }}
        >
          {/* Search bar */}
          <div
            style={{
              padding: '8px 8px 7px',
              borderBottom: `1px solid ${T.colorSeparator}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 10px',
                background: T.colorBgSearch,
                border: `1px solid ${T.colorBorder}`,
                borderRadius: T.radiusSearch,
              }}
            >
              <SearchIcon size={13} color={T.colorTextMuted} />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search country or code…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setHighlight(0);
                }}
                aria-label="Search countries"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontFamily: T.fontFamily,
                  fontSize: 13,
                  color: T.colorText,
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 0,
                    color: T.colorTextMuted,
                  }}
                >
                  <XIcon size={11} color={T.colorTextMuted} />
                </button>
              )}
            </div>
          </div>

          {/* Country list */}
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Countries"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: '4px 0',
              maxHeight: 220,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
            }}
          >
            {filteredCountries.length === 0 ? (
              <li
                style={{
                  padding: '12px 16px',
                  fontSize: 13,
                  color: T.colorTextMuted,
                  textAlign: 'center',
                }}
              >
                No countries found
              </li>
            ) : (
              filteredCountries.map((country, idx) => {
                const isSelected = country.code === selectedCountry.code;
                const isHighlighted = idx === highlight;
                return (
                  <CountryOption
                    key={`${country.code}-${country.dialCode}`}
                    country={country}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    onSelect={selectCountry}
                    onHover={() => setHighlight(idx)}
                  />
                );
              })
            )}
          </ul>
        </div>
      )}

      {/* Sub-line message */}
      {(error || hint) && (
        <p
          id={error ? `${id}-error` : `${id}-hint`}
          role={error ? 'alert' : undefined}
          aria-live={error ? 'polite' : undefined}
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.4,
            color: error ? T.colorTextError : T.colorTextHint,
          }}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
});

export default EssealPhoneInput;

// ─── CountryOption (extracted to keep hover state local) ─────────────────────

function CountryOption({
  country,
  isSelected,
  isHighlighted,
  onSelect,
  onHover,
}: {
  country: Country;
  isSelected: boolean;
  isHighlighted: boolean;
  onSelect: (c: Country) => void;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = isHighlighted ? '#eef2ff' : hover ? '#f4f4f5' : 'transparent';

  return (
    <li
      role="option"
      aria-selected={isSelected}
      onMouseEnter={() => {
        setHover(true);
        onHover();
      }}
      onMouseLeave={() => setHover(false)}
      onClick={() => onSelect(country)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 12px',
        cursor: 'pointer',
        background: bg,
        transition: 'background 80ms ease',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <span role="img" aria-hidden="true" style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
        {country.flag}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 13,
          color: isSelected ? '#4338ca' : '#18181b',
          fontWeight: isSelected ? 500 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {country.name}
      </span>
      <span
        style={{
          fontFamily: "'SF Mono', 'Fira Code', Consolas, monospace",
          fontSize: 11,
          color: '#a1a1aa',
          flexShrink: 0,
        }}
      >
        {country.dialCode}
      </span>
      {isSelected && <CheckIcon size={12} color="#6366f1" />}
    </li>
  );
}

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

function ChevronIcon({ size, color, rotate }: { size: number; color: string; rotate?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      style={{
        transform: rotate ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 150ms ease',
        flexShrink: 0,
      }}
    >
      <path d="M2 4l4 4 4-4" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="7" cy="7" r="5" stroke={color} strokeWidth="1.5" />
      <path d="M11 11l3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function XIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M1 1l10 10M11 1L1 11" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M1.5 6l3.5 3.5L10.5 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckCircleIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8.5" stroke={color} strokeWidth="1.5" />
      <path d="M6.5 10l2.5 2.5 5-5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AlertCircleIcon({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8.5" stroke={color} strokeWidth="1.5" />
      <path d="M10 6.5v4" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="13.5" r="0.8" fill={color} />
    </svg>
  );
}
